import { useEffect, useMemo, useState } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";

import {
  hasFencedCode,
  highlightCode,
  highlighterReady,
  loadHighlighter,
} from "@/lib/highlight";
import { cn } from "@/lib/utils";

marked.setOptions({ breaks: true, gfm: true });

// FEAT-17: a fenced block is syntax-coloured when the highlighter chunk is
// loaded. `marked.parse` is synchronous, so the language a code QUESTION falls
// back to (an untagged fence) is handed to the renderer through this module
// variable, set and cleared around the one parse call below.
let fallbackLanguage: string | undefined;

marked.use({
  renderer: {
    code({ text, lang }) {
      const tag = (lang ?? "").match(/^\S+/)?.[0];
      const out = highlightCode(text.replace(/\n$/, ""), tag, fallbackLanguage);
      // false = marked's own plain <pre><code> — the pre-FEAT-17 output.
      if (!out) return false;
      // `out.html` is highlight.js output (the code's text, escaped, in class
      // spans) and `out.language` is a registered grammar name, never the raw
      // fence tag. DOMPurify still sanitises the whole document below.
      return `<pre><code class="hljs language-${out.language}">${out.html}\n</code></pre>\n`;
    },
  },
});

interface MarkdownProps {
  source: string;
  className?: string;
  /** A code question's language: what an UNTAGGED fenced block is coloured as. */
  codeLanguage?: string;
}

// Renders prompt markdown (bold, headings, lists, inline + fenced code).
// Source is recruiter-authored — workspace-global trust means one compromised
// recruiter session could plant <script> tags that fire on every candidate +
// co-recruiter view. DOMPurify strips them at the boundary, so candidate
// browsers never execute recruiter-supplied JS. Direct dompurify dep is pinned
// to >=3.4.16 (the floor that clears the IN_PLACE XSS advisories affecting
// <=3.4.15, GHSA-55q2-fjhq-7xh7 / GHSA-6688-9rhm-gjv2 — not our default-mode
// sanitize() path, so defense-in-depth; the `overrides` pin forces monaco's
// copy to the same floor); we do NOT rely on the transitive copy bundled inside
// monaco-editor's dist (unreachable via our data flow — see FUTURE-MVP).
// src/lib/dompurify-pin.test.ts guards the lockfile floor.
//
// EP-19: `<img>` is FORBIDDEN. DOMPurify's default allowlist keeps it, so
// `![](https://…)` in a prompt used to emit a cross-origin image element (the
// CSP's `img-src 'self'` blocked the fetch, but a `data:` URI would have
// rendered). A question's picture is a structured field now, served through
// our own origin (`QuestionPrompt`), so markdown never needs one.
//
// Pre-ship 2026-10-18 (appsec h19): DOMPurify's default profile also keeps
// forms, inputs, buttons and <style>, so a teammate with workspace.write (an
// external sourcer since FEAT-39) could plant a full-page "sign in again"
// form in a prompt the owner opens. A prompt is text, so none of it is
// needed. (No CSP `form-action` instead: the provider sign-in buttons POST a
// native form that redirects to Google / Microsoft.)
const SANITIZE = {
  FORBID_TAGS: ["img", "form", "input", "button", "textarea", "select", "option", "style"],
  FORBID_ATTR: ["style", "action", "formaction"],
};

/** The prompt's HTML exactly as `Markdown` renders it — exported for tests. */
export function markdownToSafeHtml(source: string, codeLanguage?: string): string {
  fallbackLanguage = codeLanguage;
  try {
    return DOMPurify.sanitize(marked.parse(source) as string, SANITIZE);
  } finally {
    fallbackLanguage = undefined;
  }
}

export function Markdown({ source, className, codeLanguage }: MarkdownProps) {
  // FEAT-17: the first prompt with a fenced block pulls the highlighter chunk;
  // the block renders plain meanwhile and re-renders coloured when it lands.
  // A chunk that never arrives leaves it plain — colour is never a gate.
  const [coloured, setColoured] = useState(highlighterReady);
  const wantsColour = hasFencedCode(source);
  useEffect(() => {
    if (coloured || !wantsColour) return;
    let cancelled = false;
    void loadHighlighter().then((ok) => {
      if (ok && !cancelled) setColoured(true);
    });
    return () => {
      cancelled = true;
    };
  }, [coloured, wantsColour]);

  const html = useMemo(
    () => markdownToSafeHtml(source, codeLanguage),
    // `coloured` is a real input: the same source renders differently once the
    // highlighter is loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, codeLanguage, coloured],
  );

  return (
    <div
      className={cn(
        "prompt-markdown max-w-none text-sm leading-relaxed",
        "[&_h1]:mt-0 [&_h1]:mb-3 [&_h1]:text-xl [&_h1]:font-semibold",
        "[&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold",
        "[&_h3]:mt-3 [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold",
        "[&_p]:mb-3 last:[&_p]:mb-0",
        "[&_strong]:font-semibold",
        "[&_em]:italic",
        "[&_ul]:mb-3 [&_ul]:ml-6 [&_ul]:list-disc",
        "[&_ol]:mb-3 [&_ol]:ml-6 [&_ol]:list-decimal",
        "[&_code]:rounded [&_code]:bg-secondary [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-code",
        "[&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-secondary [&_pre]:p-4 [&_pre_code]:bg-transparent [&_pre_code]:p-0",
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export default Markdown;
